import React, { useEffect, useState } from 'react';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { Container, Form, Button, Alert, Spinner } from 'react-bootstrap';
import PasswordInput from '../common/PasswordInput';
import { auth } from '../../firebaseConfig';

const PasswordReset: React.FC = () => {
    const [oobCode, setOobCode] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('oobCode');
        setOobCode(code);

        if (code) {
            verifyPasswordResetCode(auth, code)
                .then(email => {
                    setEmail(email);
                    setLoading(false);
                })
                .catch(() => {
                    setError('Invalid or expired password reset link.');
                    setLoading(false);
                });
        } else {
            setError('Missing password reset code.');
            setLoading(false);
        }
    }, []);

    const handleSubmit = async () => {

        setError(null);

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (oobCode) {
            try {
                await confirmPasswordReset(auth, oobCode, newPassword);
                setSuccess(true);
            } catch (err: any) {
                setError(err.message || 'Failed to reset password.');
            }
        }
    };

    const passwordMismatch = newPassword !== confirmPassword;

    return (
        <Container className="mt-5" style={{ maxWidth: '500px' }}>
            <h3 className="mb-4">Reset Your Password</h3>

            {loading && <Spinner animation="border" />}

            {!loading && error && <Alert variant="danger">{error}</Alert>}

            {!loading && success && (
                <Alert variant="success">
                    Your password has been reset successfully. You can now log in with your new password.
                </Alert>
            )}

            {!loading && !success && !error && (
                <>
                    <Form.Group controlId="email" className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control type="email" value={email ?? ''} disabled />
                    </Form.Group>

                    <Form.Group controlId="newPassword" className="mb-3">
                        <PasswordInput
                            label='New Password'
                            onChange={e => setNewPassword(e)}
                            isInvalid={newPassword.length < 6}
                            value={newPassword}
                            feedback='Password must be at least 6 characters'
                        />
                    </Form.Group>

                    <Form.Group controlId="confirmPassword" className="mb-3">
                        <Form.Label>Confirm Password</Form.Label>
                        <Form.Control
                            type="password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            isInvalid={passwordMismatch}
                        />
                        <Form.Control.Feedback type="invalid">
                            Passwords do not match.
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Button type="submit" variant="primary" onClick={handleSubmit}>
                        Reset Password
                    </Button>
                </>
            )}
        </Container>
    );
};

export default PasswordReset;